import * as React from 'react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface Option {
	value: string;
	label: string;
	icon?: string;
	description?: string;
	className?: string;
	labelClassName?: string;
	descriptionClassName?: string;
}

interface SlideSelectorProps {
	value: string;
	onChange: (value: string) => void;
	options: Option[];
	className?: string;
	activeColor?: string;
}

export function SlideSelector({
	value,
	onChange,
	options,
	className,
	activeColor = 'purple',
}: SlideSelectorProps) {
	const activeOption = options.find((opt) => opt.value === value);
	const activeIndex = options.findIndex((opt) => opt.value === value);

	return (
		<div
			className={cn(
				'relative rounded-full p-1 bg-neutral-800 dark:bg-neutral-900 border-2 border-neutral-700 dark:border-neutral-800',
				'shadow-md',
				className
			)}
		>
			<div className='relative z-0 flex h-20'>
				{options.map((option) => (
					<button
						key={option.value}
						onClick={() => onChange(option.value)}
						className='relative z-10 flex-1 flex flex-col items-center py-4'
					>
						<div className='flex flex-col items-center gap-0 text-neutral-500'>
							<div className='flex items-center gap-3'>
								<span className='text-2xl'>{option.icon}</span>
								<span className='whitespace-nowrap text-lg font-bold uppercase tracking-wide'>
									{option.label}
								</span>
							</div>
							{option.description && (
								<span className='text-sm whitespace-nowrap -mt-1 opacity-70'>
									{option.description}
								</span>
							)}
						</div>
					</button>
				))}

				<motion.div
					className={cn(
						'absolute inset-1 rounded-full flex flex-col items-center py-4 z-20',
						activeOption?.className || `bg-${activeColor}-600`
					)}
					initial={false}
					animate={{
						x: `${activeIndex * 98.5}%`,
					}}
					transition={{
						type: 'spring',
						stiffness: 200,
						damping: 25,
						mass: 1,
						restDelta: 0.001,
						restSpeed: 0.001,
						bounce: 0.1,
					}}
					style={{
						width: `${100 / options.length}%`,
					}}
				>
					<div className='flex flex-col items-center gap-0 -mt-1'>
						<div className='flex items-center gap-3'>
							<span className='text-2xl text-foreground'>
								{activeOption?.icon}
							</span>
							<span className='whitespace-nowrap text-lg font-bold text-foreground uppercase tracking-wide'>
								{activeOption?.label}
							</span>
						</div>
						{activeOption?.description && (
							<span className='text-sm text-muted-foreground whitespace-nowrap -mt-1'>
								{activeOption.description}
							</span>
						)}
					</div>
				</motion.div>
			</div>
		</div>
	);
}
